import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCanManageTemplates } from '@/hooks/usePermissions';
import { usePropertyTypesInfiniteQuery } from '@/hooks/queries/usePropertiesTemplates';
import { useInfiniteScrollFetch } from '@/hooks/useInfiniteScrollFetch';
import { TemplatesHeader } from './components/templates-header';
import { PropertyTypeCard } from './components/property-type-card';
import { AddPropertyTypeCard } from './components/add-property-type-card';
import { AddPropertyTypeDialog } from './add-property-type-dialog';
import { EditPropertyTypeDialog } from './edit-property-type-dialog';
import { DeleteConfirmationDialog } from './delete-confirmation-dialog';
import { ICON_MAP, COLOR_MAP } from './constants';
import type { ApiPropertyType } from '@/types/api';

export default function TemplatesPage() {
  const { i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Permission check
  const canManage = useCanManageTemplates();

  // Dialog states
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [editingPropertyType, setEditingPropertyType] =
    useState<ApiPropertyType | null>(null);
  const [deletingPropertyType, setDeletingPropertyType] =
    useState<ApiPropertyType | null>(null);

  // Fetch property types with infinite scroll
  const infiniteQuery = usePropertyTypesInfiniteQuery({ page_size: 20 });
  const {
    items: propertyTypes,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    sentinelRef,
  } = useInfiniteScrollFetch({
    infiniteQuery,
  });

  return (
    <div
      className={cn(
        'min-h-screen bg-gradient-to-br from-background to-muted/20',
        isRTL && 'rtl'
      )}
    >
      {/* Header */}
      <TemplatesHeader propertyTypesCount={propertyTypes.length} />

      <div className="max-w-7xl mx-auto p-8">
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {/* Property Type Cards */}
        {!isLoading && (
          <div
            className={cn(
              'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8',
              isRTL && 'rtl'
            )}
          >
            {propertyTypes.map(propertyType => (
              <PropertyTypeCard
                key={propertyType.id}
                propertyType={propertyType}
                iconMap={ICON_MAP}
                colorMap={COLOR_MAP}
                onEdit={canManage ? setEditingPropertyType : undefined}
                onDelete={canManage ? setDeletingPropertyType : undefined}
              />
            ))}

            {/* Add New Property Type Card - Only for super_admin */}
            {canManage && (
              <AddPropertyTypeCard onClick={() => setIsAddDialogOpen(true)} />
            )}
          </div>
        )}

        {/* Infinite Scroll Sentinel */}
        {hasNextPage && (
          <div ref={sentinelRef} className="flex justify-center py-8">
            {isFetchingNextPage && (
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            )}
          </div>
        )}
      </div>

      {/* Dialogs */}
      <AddPropertyTypeDialog
        open={isAddDialogOpen}
        onOpenChange={setIsAddDialogOpen}
      />

      <EditPropertyTypeDialog
        open={!!editingPropertyType}
        onOpenChange={open => !open && setEditingPropertyType(null)}
        propertyType={editingPropertyType}
      />

      <DeleteConfirmationDialog
        open={!!deletingPropertyType}
        onOpenChange={open => !open && setDeletingPropertyType(null)}
        propertyType={deletingPropertyType}
      />
    </div>
  );
}
